/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import ApplicationFeatureFlags from '@ion/core/src/security/ApplicationFeatureFlags';
import {getClient} from '@ion/services/src/graphql/GraphQLConfig';
import APM from '@ion/telemetry/src/apm/APM';
import {autoMonitorPagePerformance} from '@ion/telemetry/src/apm/automation/PagePerformanceAutoMonitor';
import {registerGraphQLClient} from '@ion/telemetry/src/apm/monitors/GraphQLMonitor';

/**
 * Defines the feature flags used to control the Application Performance Monitoring
 * (APM) Framework behavior within the application.
 *
 * ```typescript
 *   import {APMFeatureFlag} from '@ion/applications/src/telemetry/APMInitializer';
 *   ...
 *
 *   // determine if the apm framework is enabled ...
 *   const isAPMEnabled = FeatureFlags.isEnabled(APMFeatureFlag.APM);
 *
 *   // determine if the apm framework is enabled in debug only mode ...
 *   const isAPMEnabled = FeatureFlags.isEnabled(APMFeatureFlag.APM_DEBUG);
 * ```
 */
export enum APMFeatureFlag {
  /**
   * Feature flag to fully enable the APM APM Framework with metrics collection and
   * transmission to external endpoints. When enabled via the URL parameter
   * `?features=apm`, the framework will collect performance metrics and send
   * them to configured endpoints.
   */
  APM = 'apm',
  /**
   * Feature flag to enable the APM Framework in debug mode only, where metrics are
   * collected and logged, but not emitted to external endpoints. When enabled via
   * the URL parameter `?features=apmdebug`, the APM Framework will only log metrics
   * for automated test script comparisons against baseline measurements, as well as
   * for debugging purposes.
   */
  APM_DEBUG = 'apmdebug'
}

/**
 * Represents the results of checking APM framework enablement status, indicating
 * whether the APM framework should be enabled and if so, in what mode should it
 * be enabled (either debug only, or debug and emission modes, respectively).
 *
 * ```typescript
 *  import {getAPMEnablement} from '@ion/applications/src/telemetry/APMInitializer';
 *  ...
 *
 *  const featureFlags = new ApplicationFeatureFlags();
 *  const {enableAPMFramework, enableAPMDebugOnly} = getAPMEnablement(featureFlags);
 *
 *  if (enableAPMFramework) {
 *    // Initialize APM in full mode
 *  } else if (enableAPMDebugOnly) {
 *    // Initialize APM in debug-only mode
 *  }
 * ```
 */
export interface GetAPMEnablementResults {
  /**
   * Indicates whether the APM framework should be fully enabled with metrics
   * collection and transmission to external endpoints.
   */
  enableAPMFramework: boolean;
  /**
   * Indicates whether the APM framework should be enabled in debug-only mode,
   * where metrics are collected and logged but not transmitted externally.
   */
  enableAPMDebugOnly: boolean;
}

/**
 * Provides an abstraction for determining if the APM Framework is to be enabled.
 * Currently, the Framework can be fully enabled via the APM Feature Flags; i.e.
 * "?features=apm", or enabled but only to provide logging of APM metrics; that
 * is, the measurements will not be emitted to any external endpoints. This mode
 * is enabled via the APM_DEBUG Feature Flag; i.e. "?features=apmdebug".
 *
 * One the APM Framework is to be enabled by default (i.e. not hidden behind a
 * Feature Flag), this function can be updated as follows:
 *
 * ```typescript
 *  // _app.tsx
 *  import {
 *    getAPMEnablement,
 *    initializeAPM
 *   } from '../telemetry/apm';
 *   ...
 *
 *   export const getAPMEnablement = () => ({
 *     enableAPMFramework : true,
 *     enableAPMDebugOnly : false
 *   });
 * ```
 */
export const getAPMEnablement = (api: ApplicationFeatureFlags): GetAPMEnablementResults => ({
  enableAPMFramework : api.isEnabled(APMFeatureFlag.APM),
  enableAPMDebugOnly : api.isEnabled(APMFeatureFlag.APM_DEBUG)
});

/**
 * Initializes the Application Performance Monitoring (APM) Framework based on the
 * provided feature flags. This function checks if either the APM or APM_DEBUG flags
 * are enabled and configures the framework accordingly.
 *
 * ```typescript
 *  import {initializeAPM} from '@ion/applications/src/telemetry/APMInitializer';
 *  import ApplicationFeatureFlags from '@ion/core/src/security/ApplicationFeatureFlags';
 *  import FeaturesFlags from '../security/FeatureFlags';
 *  ...
 *
 *  const isAPMInitialized = initializeAPM(FeaturesFlags);
 *
 *  if (isAPMInitialized) {
 *    // APM framework is now active (either in full or debug mode)
 *    logger.info('APM Monitoring has been activated');
 *  }
 * ```
 *
 * @param   api The ApplicationFeatureFlags instance used to check if APM features
 *          are enabled via URL parameters or other configuration mechanisms.
 * @returns `true` if the APM Framework was initialized (either in full or debug mode),
 *          otherwise `false` if neither APM feature flag was enabled.
 */
export const initializeAPM = (api: ApplicationFeatureFlags): boolean => {
  const {enableAPMFramework, enableAPMDebugOnly} = getAPMEnablement(api);
  if (enableAPMFramework || enableAPMDebugOnly) {
    APM.initialize(true, null, !enableAPMDebugOnly);
    autoMonitorPagePerformance();
    return true;
  }
  return false;
};

/**
 * Registers the additional Service Monitor clients with the Application Performance
 * Monitoring (APM) Framework to enable monitoring and telemetry for specific services
 * such as GraphQL operations.
 *
 * This function should be called after the APM framework has been initialized via
 * the `initializeAPM` function.
 *
 * ```typescript
 *  import {
 *    initializeAPM,
 *    initializeAPMClients
 *  } from '@ion/applications/src/telemetry/APMInitializer';
 *  import ApplicationFeatureFlags from '@ion/core/src/security/ApplicationFeatureFlags';
 *  ...
 *
 *  // first initialize the APM framework ...
 *  const featureFlags = new ApplicationFeatureFlags();
 *  const isInitialized = initializeAPM(featureFlags);
 *
 *  // then register additional clients if the framework has been initialized ...
 *  if (isInitialized) {
 *    initializeAPMClients();
 *    logger.info('APM clients registered for monitoring');
 *  }
 * ```
 *
 * @see {@link initializeAPM} Should be called before this function
 * @see {@link registerGraphQLClient} Used internally to register the GraphQL client
 * @see {@link getClient} Used to retrieve the GraphQL client instance
 */
export const initializeAPMClients = () => {
  registerGraphQLClient(getClient());
};
